import * as API from '@/API';
import CheckModel from '@/Models/Check/CheckModel';
import type FilingCabinetStore from '@/Stores/FilingCabinetStore';
import { isNullOrWhiteSpace } from '@/Utility/StringFunctions';
import { t } from 'i18next';
import { makeAutoObservable } from 'mobx';

interface StateData {
    check: CheckModel;
    currentCustomInput: string;
    typeAge: string;
    minAge: string;
    maxAge: string;
    dropDownValue: number;
    errors: string[];
    serverError: string | null;
}

export default class CheckDetailStore {
    state: StateData = {
        check: {
            Id: 0,
            AddedOn: '',
            LastModificationDateTime: '',
            ModifiedBy: '',
            Name: '',
            Remarks: '',
            CopyRemarksToOrder: false,
            CopyRemarksToPatient: false,
            Global: false,
            ValidFrom: '',
            ValidTo: '',
            Gender: null as any,
            MinEffectiveAge: undefined,
            MaxEffectiveAge: undefined,
            MinYearAge: undefined,
            MaxYearAge: undefined,
            RequiredToChangeToStatus: false,
            VisibleInTechnicianScreen: false,
            TypeCode: 'DDL',
            Unit: '',
            RequiredToSaveAppointment: false,
            CustomDropdownValues: [],
            RequiredToChangeToStatusStatus: 0,
            Obligatory: false,
        },
        currentCustomInput: '',
        typeAge: 'EffAge',
        minAge: '',
        maxAge: '',
        dropDownValue: 0,
        errors: [],
        serverError: null,
    };

    id: string | undefined;
    loading = true;

    private readonly fcStore: FilingCabinetStore<StateData>;

    constructor(id: string | undefined, fcStore: FilingCabinetStore<StateData>) {
        this.id = id;
        this.fcStore = fcStore;

        makeAutoObservable(this);
        this.fcStore.initializeState(this.initOld, this.initNew);
    }

    private readonly initOld = (state: StateData): void => {
        this.setState(state);
        this.setLoading(false);
    };

    private readonly initNew = async (): Promise<void> => {
        this.setLoading(true);
        if (this.id === undefined) {
            this.createEmpty();
        }
        else {
            await this.loadExisting();
        }
        this.setLoading(false);
    };

    private readonly createEmpty = (): void => {
        this.setState({
            check: {
                Id: 0,
                AddedOn: '',
                LastModificationDateTime: '',
                ModifiedBy: '',
                Name: '',
                Remarks: '',
                CopyRemarksToOrder: false,
                CopyRemarksToPatient: false,
                Global: false,
                ValidFrom: '',
                ValidTo: '',
                Gender: null as any,
                MinEffectiveAge: undefined,
                MaxEffectiveAge: undefined,
                MinYearAge: undefined,
                MaxYearAge: undefined,
                RequiredToChangeToStatus: false,
                VisibleInTechnicianScreen: false,
                TypeCode: 'DDL',
                Unit: '',
                RequiredToSaveAppointment: false,
                CustomDropdownValues: [],
                RequiredToChangeToStatusStatus: 0,
                Obligatory: false,
            },
            currentCustomInput: '',
            typeAge: 'EffAge',
            minAge: '',
            maxAge: '',
            dropDownValue: 0,
            errors: [],
            serverError: null,
        });
        this.fcStore.updateTitle(t('Check'));
    };

    private readonly loadExisting = async (): Promise<void> => {
        if (this.id === undefined) {
            return;
        }

        await API.checks.get(this.id)
            .then((res) => this.setCheckModel(res));

        this.fcStore.updateTitle(`${t('Check')} - ${this.state.check.Name}`);
    };

    setState = (newState: StateData): void => {
        this.state = newState;
        this.fcStore.updateState(this.state);
    };

    set = <K extends keyof StateData>(prop: K, val: any): void => {
        this.state[prop] = val;
        this.fcStore.updateState(this.state);
    };

    setCheckAttribute = <K extends keyof CheckModel>(prop: K, val: any): void => {
        this.state.check[prop] = val;
        this.fcStore.updateState(this.state);
    };

    setAddCheckModelAges = (): void => {
        this.state.check.MinEffectiveAge = undefined;
        this.state.check.MaxEffectiveAge = undefined;
        this.state.check.MinYearAge = undefined;
        this.state.check.MaxYearAge = undefined;

        switch (this.state.typeAge) {
            case 'EffAge':
                this.state.check.MinEffectiveAge = Number.parseFloat(this.state.minAge);
                this.state.check.MaxEffectiveAge = Number.parseFloat(this.state.maxAge);
                break;
            case 'YearAge':
                this.state.check.MinYearAge = Number.parseFloat(this.state.minAge);
                this.state.check.MaxYearAge = Number.parseFloat(this.state.maxAge);
                break;
        }
        this.fcStore.updateState(this.state);
    };

    resetCustomDropdownValues = (): void => {
        this.state.check.CustomDropdownValues.length = 0;
        this.fcStore.updateState(this.state);
    };

    setLoading = (val: boolean): void => {
        this.loading = val;
    };

    setCheckModel = (check: CheckModel | null): void => {
        if (check == null) {
            return;
        }
        check.RequiredToSaveAppointment = check.RequiredToSaveAppointment ?? false;

        this.state.check = check;

        this.state.typeAge = (check.MinEffectiveAge !== undefined && check.MaxEffectiveAge !== undefined) ? 'EffAge' : 'YearAge';
        this.state.minAge = (check.MinEffectiveAge ?? check.MinYearAge ?? '') as string;
        this.state.maxAge = (check.MaxEffectiveAge ?? check.MaxYearAge ?? '') as string;

        this.fcStore.updateState(this.state);
    };

    setValidFrom = (value: string | Date | null): void => {
        if (typeof value == 'string' && !isNullOrWhiteSpace(value)) {
            value = new Date(value);
        }

        this.state.check.ValidFrom = value == null ? null : (value as Date).toISOString();
    };

    setValidTo = (value: string | Date | null): void => {
        if (typeof value == 'string' && !isNullOrWhiteSpace(value)) {
            value = new Date(value);
        }

        this.state.check.ValidTo = value == null ? null : (value as Date).toISOString();
    };

    removeCustomDropdownOption = (index: number) => {
        this.state.check.CustomDropdownValues.splice(index, 1);
        this.fcStore.updateState(this.state);
    };

    addError = (field: string): void => {
        this.state.errors = [...this.state.errors, field];
    };

    clearErrors = (): void => {
        this.state.errors = [];
    };

    isError = (field: string): boolean => {
        return this.state.errors.find((x) => x == field) != null;
    };

    setServerError = (str: string | null): void => {
        this.state.serverError = str;
    };
}
